import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const USER_TYPES = [
  { value: 'student', label: 'Student', desc: 'Currently enrolled in college' },
  { value: 'high_school_senior', label: 'High School Senior', desc: 'Incoming freshman' },
  { value: 'young_adult', label: 'Young Adult', desc: 'Recent grad or young professional' },
  { value: 'landlord', label: 'Landlord', desc: 'I own or manage properties' },
];

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    school: 'University of Iowa',
    user_type: searchParams.get('type') || 'student',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step === 1) { setStep(2); return; }
    setError('');
    setLoading(true);
    try {
      await signup(form);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Signup failed');
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-[#F4F4F4]">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="w-9 h-9 bg-[#C8A800] rounded-lg flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-white">
                <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
              </svg>
            </div>
            <Link to="/" className="text-xl font-bold text-[#1B1B1B] tracking-tight" style={{ fontFamily: 'Georgia, serif' }}>NestHawk</Link>
          </div>
          <h2 className="text-base font-semibold text-[#1B1B1B] mt-3">Create your account</h2>
          <div className="flex items-center justify-center gap-2 mt-3">
            {[1, 2].map(s => (
              <div key={s} className={`h-1.5 rounded-full transition-all duration-300 ${s === step ? 'w-8 bg-[#C8A800]' : 'w-4 bg-[#DDDDDD]'}`} />
            ))}
          </div>
        </div>

        <div className="card p-8">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-5 text-red-600 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {step === 1 ? (
              <div className="space-y-5">
                <div>
                  <label className="label">Full Name</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="John Doe"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="label">Email</label>
                  <input
                    type="email"
                    className="input"
                    placeholder="you@uiowa.edu"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="label">Password</label>
                  <input
                    type="password"
                    className="input"
                    placeholder="Min. 6 characters"
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    required
                    minLength={6}
                  />
                </div>
                <button type="submit" className="btn-primary w-full">Continue</button>
              </div>
            ) : (
              <div className="space-y-5">
                <div>
                  <label className="label">School</label>
                  <input
                    type="text"
                    className="input"
                    value={form.school}
                    onChange={e => setForm({ ...form, school: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="label">I am a...</label>
                  <div className="grid grid-cols-1 gap-2 mt-1">
                    {USER_TYPES.map(type => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setForm({ ...form, user_type: type.value })}
                        className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                          form.user_type === type.value
                            ? 'border-[#C8A800] bg-[#C8A800]/10'
                            : 'border-[#DDDDDD] hover:border-[#BBBBBB]'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                          form.user_type === type.value ? 'border-[#C8A800] bg-[#C8A800]' : 'border-[#CCCCCC]'
                        }`} />
                        <div>
                          <p className="font-medium text-[#1B1B1B] text-sm">{type.label}</p>
                          <p className="text-xs text-[#777777]">{type.desc}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button type="button" onClick={() => setStep(1)} className="btn-ghost flex-1 border border-[#DDDDDD]">
                    Back
                  </button>
                  <button type="submit" disabled={loading} className="btn-primary flex-1">
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-[#C8A800] border-t-transparent rounded-full animate-spin" />
                        Creating...
                      </span>
                    ) : 'Create Account'}
                  </button>
                </div>
              </div>
            )}
          </form>

          <p className="text-center text-[#555555] mt-6 text-sm nav-link">
            Already have an account?{' '}
            <Link to="/login" className="text-[#C8A800] hover:text-[#B89700] font-medium">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
